import React, { useState, useEffect } from "react";
import {
  FileText,
  Zap,
  Shield,
  Clipboard,
  Truck,
  Check,
  Building2,
  Edit2,
  Save,
  X,
} from "lucide-react";
import {
  getChecklistForJob,
  saveCheckList,
  updateCheckListName,
} from "./utils";
import { useNotification } from "./CustomNotificationSystem";

const Checklist = ({ jobId, jobseeker }) => {
  const [hiringStages, setHiringStages] = useState([]);
  const [editingTask, setEditingTask] = useState(null); // { stageIndex, taskId }
  const [editingText, setEditingText] = useState("");
  const notify = useNotification();

  useEffect(() => {
    // Set up hiring stages with tasks specific to hiring an electrician
    async function getCheckList(jobId) {
      const job_checklist = await getChecklistForJob(jobId);
      const preDefinedChecklist = [
        {
          name: "Pre-Employment",
          tasks: [
            { id: 15, text: "Background check", completed: false },
            { id: 16, text: "Contract finalised", completed: false },
            { id: 18, text: "First day orientation", completed: false },
          ],
        },
        {
          name: "Final Preparations",
          tasks: [
            { id: 19, text: "WhatsApp reminder", completed: false },
            { id: 20, text: "Transportation confirmed", completed: false },
            { id: 21, text: "First day instructions", completed: false },
            { id: 22, text: "Supervisor informed", completed: false },
          ],
        },
      ];

      let checklist;
      if (jobseeker.hiring_checklist && jobseeker.hiring_checklist.length > 0) {
        checklist = jobseeker.hiring_checklist;
      } else {
        checklist = [...job_checklist, ...preDefinedChecklist];
      }
      setHiringStages(checklist);
    }
    getCheckList(jobId);
  }, [jobId]);

  const toggleTask = (stageIndex, taskId) => {
    setHiringStages((prevStages) => {
      // Copy the previous stages array
      const newStages = [...prevStages];

      // Copy the tasks array immutably
      const newTasks = [...newStages[stageIndex].tasks];

      // Find the task index
      const taskIndex = newTasks.findIndex((task) => task.id === taskId);

      // Toggle the completed status immutably
      if (taskIndex !== -1) {
        newTasks[taskIndex] = {
          ...newTasks[taskIndex],
          completed: !newTasks[taskIndex].completed,
        };

        // Update the tasks in the stage immutably
        newStages[stageIndex] = {
          ...newStages[stageIndex],
          tasks: newTasks,
        };
      }

      return newStages;
    });
  };

  const startEditingTask = (stageIndex, taskId, currentText) => {
    setEditingTask({ stageIndex, taskId, prevText: currentText });
    setEditingText(currentText);
  };

  const saveTaskEdit = async () => {
    if (editingText.trim() === "") {
      cancelEdit();
      return;
    }

    console.log(editingTask, "editingTaskeditingTaskeditingTask");

    const updated = await updateCheckListName(
      jobId,
      editingTask.prevText,
      editingText,
    );

    if (updated) {
      setHiringStages((prevStages) => {
        const newStages = [...prevStages];
        const newTasks = [...newStages[editingTask.stageIndex].tasks];
        const taskIndex = newTasks.findIndex(
          (task) => task.id === editingTask.taskId,
        );

        if (taskIndex !== -1) {
          newTasks[taskIndex] = {
            ...newTasks[taskIndex],
            text: editingText.trim(),
          };

          newStages[editingTask.stageIndex] = {
            ...newStages[editingTask.stageIndex],
            tasks: newTasks,
          };
        }

        return newStages;
      });

      setEditingTask(null);
      setEditingText("");
    } else {
      notify("Sorry! there is an issue while saving checklist", "error");
    }
  };

  const cancelEdit = () => {
    setEditingTask(null);
    setEditingText("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      saveTaskEdit();
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const handleTaskClick = (stageIndex, taskId) => {
    // Only toggle if we're not editing this task
    if (
      !editingTask ||
      editingTask.stageIndex !== stageIndex ||
      editingTask.taskId !== taskId
    ) {
      toggleTask(stageIndex, taskId);
    }
  };

  const handleSave = async () => {
    // Logic to save the checklist state
    await saveCheckList(jobId, jobseeker._id, hiringStages);
    jobseeker.hiring_checklist = hiringStages;
    notify("Checklist is saved for the jobseeker", "success");
  };

  const category_icon = {
    "by company": <Building2 className="mr-2 text-blue-500" />,
    "documentation check": <FileText className="mr-2 text-blue-500" />,
    "experience and skills": <Zap className="mr-2 text-blue-500" />,
    "safety compliance": <Shield className="mr-2 text-blue-500" />,
    "pre-employment": <Clipboard className="mr-2 text-blue-500" />,
    "final preparations": <Truck className="mr-2 text-blue-500" />,
  };

  return (
    <div className="p-4 space-y-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
      <h3 className="text-2xl font-bold mb-6 text-indigo-800 flex items-center">
        <Clipboard className="mr-2" /> Hiring Checklist for {jobseeker.name}
      </h3>

      {/* Hiring Stages Section */}
      {hiringStages.map((stage, stageIndex) => (
        <div
          key={stageIndex}
          className="bg-white p-6 rounded-lg shadow-md mb-6"
        >
          <h4 className="text-lg font-semibold mb-4 text-indigo-800 flex items-center">
            {category_icon[stage.name.toLowerCase()]}
            {stage.name}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stage.tasks.map((task) => {
              const isEditing =
                editingTask &&
                editingTask.stageIndex === stageIndex &&
                editingTask.taskId === task.id;

              return (
                <div
                  key={task.id}
                  className={`p-3 rounded-lg shadow transition-all duration-200 group ${
                    task.completed
                      ? "bg-green-100 border-green-500"
                      : "bg-gray-100 border-gray-300"
                  } ${task.critical ? "border-l-4" : "border"}`}
                >
                  <div className="flex items-center justify-between">
                    {isEditing ? (
                      <div className="flex items-center flex-1 mr-2">
                        <input
                          type="text"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onKeyDown={handleKeyPress}
                          className="text-gray-800 bg-transparent border-b border-gray-400 focus:border-indigo-500 focus:outline-none flex-1 text-sm"
                          autoFocus
                        />
                        <div className="flex items-center ml-2 space-x-1">
                          <button
                            onClick={saveTaskEdit}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="this will save for every jobseeker"
                          >
                            <Save size={12} />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Cancel"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span
                          className={`cursor-pointer flex-1 ${task.completed ? "line-through text-gray-500" : "text-gray-800"} ${task.critical ? "font-semibold" : ""}`}
                          onClick={() => handleTaskClick(stageIndex, task.id)}
                        >
                          {task.text}
                        </span>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() =>
                              startEditingTask(stageIndex, task.id, task.text)
                            }
                            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit task"
                          >
                            <Edit2 size={12} />
                          </button>
                          {task.completed && (
                            <Check className="text-green-500" size={20} />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {task.critical && (
                    <span className="text-red-500 text-xs mt-1 block">
                      (Critical)
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={handleSave}
        className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 focus:outline-none"
      >
        Save Checklist
      </button>
    </div>
  );
};

export default Checklist;
